import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import { formatDateLong } from "@/utils";
import type {
  ClientMyExamsQueryParams,
  ClientMyExamsApiResponse,
  ClientMyExamItemResDto,
  ClientExam,
  ClientExamForTakingDto,
  ClientExamStartAttemptDto,
  ClientExamQuestionsResponseDto,
  ClientExamQuestionsItemDto,
  EnrollmentCertificateApiResponse,
  ClientMyExamEducationsApiResponse,
  ClientMyExamEducationsQueryParams,
  ClientMyExamEducationItemResDto,
  ClientExamResultApiResponse,
  ClientExamResultData,
} from "../types/client-exams.types";

const EXAMS_ME_ENDPOINT = "/api/client/educations/exam/me";
const EXAM_EDUCATIONS_ENDPOINT = "/api/client/educations/exam/me/educations";

function getExamEndpoint(educationId: string): string {
  return `/api/client/educations/${educationId}/exam`;
}

function getExamStartEndpoint(educationId: string): string {
  return `/api/client/educations/${educationId}/exam/start`;
}

function getExamQuestionsEndpoint(educationId: string): string {
  return `/api/client/educations/${educationId}/exam/questions`;
}

function getExamAnswerEndpoint(educationId: string, questionId: string): string {
  return `/api/client/educations/${educationId}/exam/answers/${questionId}`;
}

function getExamFinishEndpoint(educationId: string): string {
  return `/api/client/educations/${educationId}/exam/finish`;
}

function getExamResultEndpoint(educationId: string, attemptId: string): string {
  return `/api/client/educations/${educationId}/exam/attempts/${attemptId}/result`;
}

function derivePassedFromStatuses(examAttemptStatuses?: number[]): boolean | undefined {
  if (!examAttemptStatuses?.length) return undefined;
  if (examAttemptStatuses.includes(1)) return true;
  const last = examAttemptStatuses[examAttemptStatuses.length - 1];
  if (last === 2) return false;
  if (last === 1) return true;
  return undefined; // 0 = NOT_PARTICIPATED
}

function mapApiItemToClientExam(
  item: ClientMyExamItemResDto,
  isCompleted: boolean
): ClientExam {
  const [timeElapsed, timeTotal] = item.progressTime.split("/");
  const answered = item.answeredQuestionCount ?? 0;
  const total = item.totalQuestionCount ?? 0;
  const progressPercent = (() => {
    const p = item.progressTimePercentage;
    if (typeof p === "number" && !Number.isNaN(p)) {
      return Math.min(100, Math.max(0, Math.round(p)));
    }
    return total > 0 ? Math.round((answered / total) * 100) : 0;
  })();
  const progressFraction = `${answered}/${total}`;
  const examAttemptStatuses = item.examAttemptStatuses;
  const passed = isCompleted ? derivePassedFromStatuses(examAttemptStatuses) : undefined;
  const enrollmentId = item.enrollmentId ?? (item as { enrollmentID?: string }).enrollmentID;
  const attemptId =
    item.attemptId ?? (item as { attemptID?: string }).attemptID;

  return {
    id: item.educationId,
    attemptId,
    enrollmentId,
    examName: item.examName,
    relatedEducation: item.relatedEducationName,
    passingGrade: item.passingScore,
    maxExamFailureCount: item.maxExamFailureCount ?? undefined,
    deadline: formatDateLong(item.dueDate ?? undefined) || "-",
    isCompleted,
    timeElapsed: timeElapsed?.trim() ?? "-",
    timeTotal: timeTotal?.trim() ?? "-",
    progressPercent,
    progressFraction,
    scorePercentage: item.scorePercentage,
    examAttemptStatuses,
    passed,
    score: item.scorePercentage,
    actionType: isCompleted && passed === true && enrollmentId ? "viewCertificate" : undefined,
    attendanceRequired: item.attendanceRequired,
    attendanceComplete: item.attendanceComplete,
  };
}

/**
 * GET /api/client/educations/exam/me
 * Kullanıcının sınavlarını listeler (sayfalı, filtrelenebilir).
 */
export async function getMyExams(
  params: ClientMyExamsQueryParams
): Promise<{ items: ClientExam[]; count: number }> {
  const { data } = await apiClient.get<ClientMyExamsApiResponse>(
    EXAMS_ME_ENDPOINT,
    { params }
  );

  const rawItems = Array.isArray(data?.data) ? data.data : [];
  const count = data?.count ?? 0;
  const isCompleted = params.status === "completed";

  return {
    items: rawItems.map((item) => mapApiItemToClientExam(item, isCompleted)),
    count,
  };
}

/**
 * GET /api/client/educations/exam/me/educations
 * Sınavlarım sayfasındaki "İlişkili Eğitim" filtresi için eğitimleri listeler.
 *
 * Response: { items: [{ educationId, educationName }], ... }
 */
export async function getMyExamEducations(
  params?: ClientMyExamEducationsQueryParams
): Promise<{ items: ClientMyExamEducationItemResDto[]; count: number }> {
  const { data } = await apiClient.get<ClientMyExamEducationsApiResponse>(
    EXAM_EDUCATIONS_ENDPOINT,
    { params }
  );

  const items = (() => {
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    const nested = (data as unknown as { data?: { items?: unknown; data?: unknown } })?.data;
    if (Array.isArray(nested?.items)) return nested.items as ClientMyExamEducationItemResDto[];
    if (Array.isArray(nested?.data)) return nested.data as ClientMyExamEducationItemResDto[];
    return [];
  })();
  const count = data?.count ?? items.length;

  return { items, count };
}

/**
 * GET /api/client/educations/:educationId/exam
 * Sınav çözme sayfası için sınav detayı ve soruları getirir.
 * Backend'de yoksa 404 döner.
 */
export async function getExamForTaking(
  educationId: string
): Promise<ClientExamForTakingDto> {
  const { data: res } = await apiClient.get<unknown>(
    getExamEndpoint(educationId)
  );
  const exam: ClientExamForTakingDto | undefined =
    res && typeof res === "object" && Array.isArray((res as ClientExamForTakingDto).questions)
      ? (res as ClientExamForTakingDto)
      : (res as { data?: ClientExamForTakingDto })?.data;
  if (!exam?.questions) {
    throw new Error(i18n.t("clientExamsApi.examDataFailed"));
  }
  return exam;
}

/**
 * POST /api/client/educations/:educationId/exam/start
 * Sınava başlar; deneme kaydı döner. Hata da dönebilir.
 */
export async function startExam(
  educationId: string
): Promise<ClientExamStartAttemptDto> {
  const { data: res } = await apiClient.post<unknown>(
    getExamStartEndpoint(educationId),
    {}
  );
  const raw = res as { data?: ClientExamStartAttemptDto } | ClientExamStartAttemptDto;
  const attempt =
    raw && typeof raw === "object" && "startedAt" in raw
      ? (raw as ClientExamStartAttemptDto)
      : (raw as { data?: ClientExamStartAttemptDto })?.data;
  if (!attempt?.id) {
    throw new Error(i18n.t("clientExamsApi.startFailed"));
  }
  return attempt;
}

/**
 * GET /api/client/educations/:educationId/exam/questions
 * Start sonrası sınav sorularını ve attempt bilgisini getirir.
 * Backend data'yı bazen { attempt, questions } objesi, bazen dizi döndürebilir.
 */
export async function getExamQuestions(
  educationId: string
): Promise<ClientExamQuestionsItemDto> {
  const { data: res } = await apiClient.get<ClientExamQuestionsResponseDto>(
    getExamQuestionsEndpoint(educationId)
  );
  const data = res?.data ?? (res as unknown as { data?: ClientExamQuestionsItemDto | ClientExamQuestionsItemDto[] })?.data;
  const item: ClientExamQuestionsItemDto | undefined = Array.isArray(data)
    ? data[0]
    : data && typeof data === "object" && "attempt" in data && "questions" in data
      ? (data as ClientExamQuestionsItemDto)
      : undefined;
  if (!item?.attempt || !Array.isArray(item.questions)) {
    throw new Error(i18n.t("clientExamsApi.questionsFailed"));
  }
  return item;
}

/**
 * PUT /api/client/educations/:educationId/exam/answers/:questionId
 * Soru cevabını gönderir veya günceller.
 */
export async function submitExamAnswer(
  educationId: string,
  questionId: string,
  selectedOptionIds: string[]
): Promise<void> {
  await apiClient.put(getExamAnswerEndpoint(educationId, questionId), {
    selectedOptionIds,
  });
}

export type {
  EducationFollowUpAction,
} from "@/features/client-educations/utils/follow-up-url";
import type { EducationFollowUpAction } from "@/features/client-educations/utils/follow-up-url";

/** Sınav bitirme sonucu: attempt durumu + puan + varsa popup aksiyonu. */
export type ExamAttemptResultStatus = "PASSED" | "FAILED" | string;

export interface FinishExamResult {
  /** "PASSED" / "FAILED" — BE döndürmüyorsa undefined */
  status?: ExamAttemptResultStatus;
  /** 0-100 — BE döndürmüyorsa undefined */
  scorePercentage?: number;
  /** Sonraki aşama (anket / sınav tekrar) varsa */
  followUpAction: EducationFollowUpAction | null;
}

interface FinishExamApiPayload {
  id?: string;
  status?: ExamAttemptResultStatus;
  scorePercentage?: number;
  followUpAction?: EducationFollowUpAction | null;
}

/**
 * POST /api/client/educations/:educationId/exam/finish
 * Sınavı tamamlar. Varsa bir sonraki aşamanın (anket / sınav tekrar / sertifika)
 * yönlendirme bilgisini `followUpAction` alanında döner.
 */
export async function finishExam(
  educationId: string,
): Promise<FinishExamResult> {
  const { data } = await apiClient.post<{
    data?: FinishExamApiPayload;
    status?: ExamAttemptResultStatus;
    scorePercentage?: number;
    followUpAction?: EducationFollowUpAction | null;
  }>(getExamFinishEndpoint(educationId), {});
  const payload: FinishExamApiPayload =
    (data as { data?: FinishExamApiPayload })?.data ?? (data as FinishExamApiPayload) ?? {};
  return {
    status: payload.status,
    scorePercentage: payload.scorePercentage,
    followUpAction: payload.followUpAction ?? null,
  };
}

/**
 * GET /api/client/educations/:educationId/exam/attempts/:attemptId/result
 * Tamamlanmış sınav denemesi özeti ve soru/cevap özeti.
 */
export async function getExamResult(
  educationId: string,
  attemptId: string
): Promise<ClientExamResultData> {
  const { data: res } = await apiClient.get<ClientExamResultApiResponse | unknown>(
    getExamResultEndpoint(educationId, attemptId)
  );
  const raw = res as ClientExamResultApiResponse & { data?: ClientExamResultData };
  const payload = raw?.data;
  if (!payload?.attempt || !Array.isArray(payload.questions)) {
    throw new Error(i18n.t("clientExamsApi.resultFailed"));
  }
  return payload;
}

/**
 * GET /api/client/educations/:educationId/enrollments/:enrollmentId/certificate
 * Kayıt (enrollment) için sertifika detayı ve parsedHtml döner.
 */
export async function getEnrollmentCertificate(
  educationId: string,
  enrollmentId: string
): Promise<EnrollmentCertificateApiResponse["data"]> {
  const { data } = await apiClient.get<EnrollmentCertificateApiResponse>(
    `/api/client/educations/${educationId}/enrollments/${enrollmentId}/certificate`
  );
  const payload = data?.data ?? (data as unknown as { data?: EnrollmentCertificateApiResponse["data"] })?.data;
  if (!payload) {
    throw new Error(i18n.t("clientExamsApi.certificateFailed"));
  }
  return payload;
}

export const clientExamsApi = {
  getMyExams,
  getMyExamEducations,
  getExamForTaking,
  startExam,
  getExamQuestions,
  submitExamAnswer,
  finishExam,
  getExamResult,
  getEnrollmentCertificate,
};
